import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { z } from "zod";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EditPageLayout } from "@/components/shared/EditPageLayout";
import { PageHeader } from "@/components/shared/PageHeader";
import { apiBaseUrl } from "@/services/apiClient";
import { tenantKeys } from "@/lib/queryKeys";
import {
  getTenantById,
  updateTenant,
  uploadTenantImage,
  type PlatformTenantDetail,
  type UpdateTenantPayload,
} from "@/services/platformTenantsService";

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const tenantFormSchema = z.object({
  name: z.string().min(1, "tenants.edit.form.nameRequired"),
  legalName: z.string().optional(),
  address: z.string().optional(),
  language: z.enum(["en", "es"]),
  industry: z.string().optional(),
  dgiEnv: z.enum(["test", "prod"]).nullable().optional(),
});

type TenantFormValues = z.infer<typeof tenantFormSchema>;

const LANGUAGE_OPTIONS = [
  { value: "en", labelKey: "tenants.edit.form.languageEn" as const },
  { value: "es", labelKey: "tenants.edit.form.languageEs" as const },
] as const;

const INDUSTRY_OPTIONS = [
  { value: "education", label: "Education" },
  { value: "agriculture", label: "Agriculture" },
  { value: "retail", label: "Retail" },
  { value: "warehouse", label: "Warehouse" },
  { value: "library", label: "Library" },
  { value: "healthcare", label: "Healthcare" },
] as const;

export function EditTenantPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fromDetail = (location.state as { from?: string } | null)?.from === "tenant-detail";
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<TenantFormValues>({
    resolver: zodResolver(tenantFormSchema),
    defaultValues: {
      name: "",
      legalName: "",
      address: "",
      language: "en",
      industry: "",
      dgiEnv: null as "test" | "prod" | null,
    },
  });

  const { data: tenant, isLoading, isError: tenantError } = useQuery({
    queryKey: tenantKeys.detail(id ?? ""),
    queryFn: () => getTenantById(id!),
    enabled: Boolean(id),
  });

  const profileImageUrl =
    tenant?.profileImage != null && tenant.profileImage !== ""
      ? tenant.profileImage.startsWith("http")
        ? tenant.profileImage
        : `${apiBaseUrl}${tenant.profileImage}`
      : null;

  useEffect(() => {
    if (tenantError) {
      toast.error(t("tenants.edit.errorLoad"));
    }
  }, [tenantError, t]);

  useEffect(() => {
    if (tenant) {
      reset({
        name: tenant.name,
        legalName: tenant.legalName ?? "",
        address: tenant.address ?? "",
        language: (tenant.language === "es" ? "es" : "en") as "en" | "es",
        industry: tenant.industry ?? "",
        dgiEnv: tenant.dgiEnv ?? null,
      });
    }
  }, [tenant, reset]);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    };
  }, [imagePreviewUrl]);

  const updateTenantMutation = useMutation({
    mutationFn: async (values: TenantFormValues) => {
      const payload: UpdateTenantPayload = {
        name: values.name,
        legalName: values.legalName || null,
        address: values.address || null,
        language: values.language,
        industry: values.industry || null,
        dgiEnv: values.dgiEnv ?? null,
      };
      await updateTenant(id!, payload);
      if (selectedFile) {
        await uploadTenantImage(id!, selectedFile);
        setSelectedFile(null);
        if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
        setImagePreviewUrl(null);
      }
    },
    onSuccess: () => {
      toast.success(t("tenants.edit.success"));
      queryClient.invalidateQueries({ queryKey: tenantKeys.detail(id!) });
      queryClient.invalidateQueries({ queryKey: tenantKeys.all });
    },
    onError: (err) => {
      const errorMessage =
        err instanceof Error ? err.message : t("tenants.edit.errorUpdate");
      toast.error(errorMessage);
    },
  });

  const handleSubmitTenant = (values: TenantFormValues): void => {
    if (id) updateTenantMutation.mutate(values);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error(t("tenants.edit.imageErrorType"));
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error(t("tenants.edit.imageErrorSize"));
      return;
    }
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setImagePreviewUrl(url);
    e.target.value = "";
  };

  const clearSelectedFile = (): void => {
    setSelectedFile(null);
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setImagePreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    void handleSubmit(handleSubmitTenant)();
  };

  const handleCancel = (): void => {
    if (fromDetail && id) {
      navigate(`/tenants/${id}`);
    } else {
      navigate("/tenants");
    }
  };

  if (!id) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        {t("tenants.edit.notFound")}
      </div>
    );
  }

  if (!tenant && !isLoading) {
    return (
      <div className="fx-content">
        <PageHeader title={t("tenants.edit.title")} onBack={handleCancel} />
        <p className="mt-4 text-sm text-muted-foreground">
          {t("tenants.edit.notFound")}
        </p>
      </div>
    );
  }

  return (
    <EditPageLayout
      title={t("tenants.edit.title")}
      description={
        tenant ? t("tenants.edit.description", { name: tenant.name }) : undefined
      }
      isLoading={isLoading}
      formId="tenant-edit-form"
      onSubmit={handleFormSubmit}
      onCancel={handleCancel}
      cancelLabel={t("tenants.edit.cancel")}
      onSave={() => void handleSubmit(handleSubmitTenant)()}
      saveLabel={t("tenants.edit.save")}
      savingLabel={t("tenants.edit.saving")}
isSaving={updateTenantMutation.isPending}
        entityExists={!!tenant}
    >
      <form
        id="tenant-edit-form"
        className="space-y-4"
        onSubmit={handleSubmit(handleSubmitTenant)}
      >
        <div className="space-y-2">
          <Label>{t("tenants.edit.imageLabel")}</Label>
          <p className="text-sm text-muted-foreground">
            {t("tenants.edit.imageHelper")}
          </p>
          <div className="flex items-center gap-4">
            <Avatar className="h-20 w-20">
              {imagePreviewUrl ? (
                <AvatarImage src={imagePreviewUrl} alt="" />
              ) : profileImageUrl ? (
                <AvatarImage src={profileImageUrl} alt="" />
              ) : null}
              <AvatarFallback className="text-lg">
                {tenant?.name?.charAt(0) ?? "?"}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleFileChange}
                disabled={updateTenantMutation.isPending}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={updateTenantMutation.isPending}
              >
                {profileImageUrl || imagePreviewUrl
                  ? t("tenants.edit.imageChange")
                  : t("tenants.edit.imageChoose")}
              </Button>
              {(imagePreviewUrl || selectedFile) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearSelectedFile}
                  disabled={updateTenantMutation.isPending}
                >
                  {t("tenants.edit.imageClear")}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="tenant-name">{t("tenants.edit.form.name")}</Label>
          <Input
            id="tenant-name"
            {...register("name")}
            placeholder={t("tenants.edit.form.namePlaceholder")}
            disabled={updateTenantMutation.isPending}
          />
          {errors.name ? (
            <p className="text-sm text-destructive">
              {t(errors.name.message ?? "tenants.edit.form.nameRequired")}
            </p>
          ) : null}
        </div>

        {tenant ? (
          <div className="grid gap-2">
            <Label>{t("tenants.page.rut")}</Label>
            <p className="text-sm text-muted-foreground">{tenant.rut}</p>
          </div>
        ) : null}

        <div className="grid gap-2">
          <Label htmlFor="tenant-legal-name">
            {t("tenants.edit.form.legalName")}
          </Label>
          <Input
            id="tenant-legal-name"
            {...register("legalName")}
            placeholder={t("tenants.edit.form.legalNamePlaceholder")}
            disabled={updateTenantMutation.isPending}
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="tenant-address">
            {t("tenants.edit.form.address")}
          </Label>
          <Input
            id="tenant-address"
            {...register("address")}
            placeholder={t("tenants.edit.form.addressPlaceholder")}
            disabled={updateTenantMutation.isPending}
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="tenant-language">
            {t("tenants.edit.form.language")}
          </Label>
          <Controller
            name="language"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={(value) =>
                  field.onChange(value as "en" | "es")
                }
                disabled={updateTenantMutation.isPending}
              >
                <SelectTrigger id="tenant-language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="tenant-industry">
            {t("tenants.edit.form.industry")}
          </Label>
          <Controller
            name="industry"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value || undefined}
                onValueChange={(value) => field.onChange(value || "")}
                disabled={updateTenantMutation.isPending}
              >
                <SelectTrigger id="tenant-industry">
                  <SelectValue placeholder={t("tenants.edit.form.industryPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(`tenants.edit.form.industryOptions.${option.value}`, option.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-4 rounded-lg border p-4">
          <div>
            <Label className="text-base">
              {t("tenants.edit.dgiEnvLabel")}
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              {t("tenants.edit.dgiEnvHelp")}
            </p>
          </div>
          <Controller
            name="dgiEnv"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value ?? "default"}
                onValueChange={(value) =>
                  field.onChange(value === "default" ? null : value)
                }
                disabled={updateTenantMutation.isPending}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">
                    {t("tenants.edit.dgiEnvDefault")}
                  </SelectItem>
                  <SelectItem value="test">
                    {t("tenants.edit.dgiEnvTest")}
                  </SelectItem>
                  <SelectItem value="prod">
                    {t("tenants.edit.dgiEnvProd")}
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          {tenant ? (
            <div className="grid gap-1">
              <Label className="text-sm text-muted-foreground">
                {t("tenants.edit.emisorElectronicoLabel")}
              </Label>
              <p className="text-sm text-foreground">
                {tenant.emisorElectronico === true
                  ? t("tenants.edit.emisorYes")
                  : tenant.emisorElectronico === false
                    ? t("tenants.edit.emisorNo")
                    : t("tenants.edit.emisorNotChecked")}
              </p>
            </div>
          ) : null}
        </div>

        <div className="space-y-4 rounded-lg border p-4">
          <div>
            <Label className="text-base">
              {t("tenants.edit.dgi.title")}
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              {t("tenants.edit.dgi.configureOnDetail")}
            </p>
            <Button variant="link" className="h-auto p-0 mt-1" asChild>
              <Link to={`/tenants/${id}?tab=factura-electronica`}>
                {t("tenants.edit.dgi.goToFacturaElectronica")}
              </Link>
            </Button>
          </div>
        </div>
      </form>
    </EditPageLayout>
  );
}
